import { css } from 'styled-components';
import Colr from 'colr';
import { buttonReset, getSmallButtonTokens, getMediumButtonTokens, getLargeButtonTokens, getPrimaryButtonTokens, getPrimaryButtonSelectedTokens, getPrimaryButtonDisabledTokens, getSecondaryButtonTokens, getSecondaryButtonSelectedTokens, getSecondaryButtonDisabledTokens, getTertiaryButtonTokens, getTertiaryButtonSelectedTokens, getTertiaryButtonDisabledTokens, getDestructiveButtonTokens, getDestructiveButtonSelectedTokens, getDestructiveButtonDisabledTokens } from '../shared';
import common, { spacing } from '../../common';
import token from '../../getToken';
export const hoverColor = color => Colr.fromHex(color).darken(10).toHex();
export const activeColor = color => Colr.fromHex(color).darken(20).toHex();
export const disabledColor = (color, percent) => {
  const opacity = percent || 0.3;
  const rgb = Colr.fromHex(color).toRgbArray().toString();
  return `rgba(${rgb}, ${opacity})`;
};
const typeScale = {
  1: '3rem',
  2: '2.125rem',
  3: '1.75rem',
  4: '1.6rem',
  5: '1.25rem',
  6: '1.125rem',
  7: '1.1rem',
  8: '1rem',
  9: '0.9375rem',
  10: '0.875rem'
};
const lineHeight = {
  tighter: 1,
  tight: 1.2,
  normal: 1.5
};
const tracking = {
  normal: 'normal'
};
const fontWeight = {
  light: 300,
  regular: 400,
  medium: 500
};
const fonts = {
  dinFont: 'DIN Next LT Pro, Arial, Helvetica, sans-serif',
  arialFont: 'Arial, Helvetica, sans-serif'
};
const colors = {
  brand: {
    base: '#912680',
    light: '#d3be96',
    dark: '#4a0053'
  },
  accent: '#3cc78b',
  shades: {
    black: '#000000',
    darkest: '#313131',
    darker: '#5e5e5e',
    dark: '#666666',
    light: '#ccced1',
    lighter: '#e4e4dd',
    lightest: '#f5f5ed',
    nearWhite: '#f7f7f7',
    white: '#ffffff'
  },
  focus: '#0072b9',
  error: '#c90400',
  success: '#3cc78b',
  info: '#912680',
  product: {
    hospital: '#912680',
    extras: '#d3be96',
    combined: '#4a0053',
    highlight: {
      backgroundColor: '#912680',
      textColor: '#e8e8e9'
    }
  }
};
const focus = {
  outlineColor: colors.focus,
  outlineStyle: 'solid',
  outlineWidth: '2px',
  outlineOffset: '0.125em'
};
const baseButtonStyles = css`
  ${buttonReset};
  font-family: ${fonts.dinFont};
  font-weight: ${fontWeight.medium};
  letter-spacing: ${tracking.normal};
  border-radius: ${token('button.dimension.border.radius', '3px')};
  border-width: ${token('button.dimension.border.width', '1px')};

  &:disabled {
    cursor: default;
    pointer-events: auto;
    border: 1px solid #c5c5c5;
    background-image: none;
    background-color: #f6f6f6;
    color: #454545;
  }
`;
const buttonStyles = {
  primary: {
    normal: css`
      ${baseButtonStyles};

      ${getPrimaryButtonTokens({
      bg: {
        default: '#3cc78b',
        hover: '#50cd97',
        active: '#3cc78b',
        disabled: disabledColor('#3cc78b', 0.6)
      },
      fg: {
        default: colors.shades.white,
        hover: colors.shades.white,
        active: colors.shades.white,
        disabled: disabledColor(colors.shades.white)
      },
      border: {
        default: '#34b57e',
        hover: '#34b57e',
        active: '#34b57e',
        disabled: '#34b57e'
      }
    })};

      background-image: linear-gradient(180deg, #50cd97 0%, #3cc78b 100%);
      box-shadow: inset 0 -1px 0 #298e62;
      text-shadow: 0 1px 0 #34b57e;

      &:hover:not([disabled]) {
        background-image: linear-gradient(180deg, #64d2a3 0%, #50cd97 100%);
      }

      &:active:not([disabled]) {
      }
    `,
    selected: css`
      ${getPrimaryButtonSelectedTokens({
      bg: '#3cc78b',
      fg: '#2ea270',
      border: '#34b57e'
    })};
    `,
    disabled: css`
      ${getPrimaryButtonDisabledTokens({
      bg: disabledColor('#3cc78b', 0.6),
      fg: disabledColor(colors.shades.white),
      border: '#c5c5c5'
    })};

      box-shadow: none;
      text-shadow: none;
      cursor: not-allowed;
    `
  },
  secondary: {
    normal: css`
      ${baseButtonStyles};

      ${getSecondaryButtonTokens({
      bg: {
        default: '#912680',
        hover: '#a52b92',
        active: '#912680',
        disabled: disabledColor(colors.shades.white)
      },
      fg: {
        default: colors.shades.white,
        hover: colors.shades.white,
        active: '#691b5c',
        disabled: disabledColor(colors.shades.white)
      },
      border: {
        default: '#54164a',
        hover: '#a52b92',
        active: '#691b5c',
        disabled: '#c5c5c5'
      }
    })};

      background-image: linear-gradient(180deg, #a52b92 0%, #912680 100%);
      box-shadow: inset 0 -1px 0 #54164a;
      text-shadow: 0 1px 0 #7d216e;

      &:hover:not([disabled]) {
        background-image: linear-gradient(180deg, #b931a4 0%, #a52b92 100%);
      }

      &:active:not([disabled]) {
      }
    `,
    selected: css`
      ${getSecondaryButtonSelectedTokens({
      bg: '#912680',
      fg: '#691b5c',
      border: '#691b5c'
    })};
    `,
    disabled: css`
      ${getSecondaryButtonDisabledTokens({
      bg: disabledColor('#912680', 0.6),
      fg: disabledColor(colors.shades.white),
      border: '#c5c5c5'
    })};

      text-shadow: none;
      box-shadow: none;
      cursor: not-allowed;
    `
  },
  tertiary: {
    normal: css`
      ${baseButtonStyles}

      ${getTertiaryButtonTokens({
      bg: {
        default: '#e4e4dd',
        hover: '#efefeb',
        active: colors.brand.base,
        disabled: disabledColor(colors.shades.white)
      },
      fg: {
        default: '#912680',
        hover: '#912680',
        active: '#912680',
        disabled: disabledColor(colors.brand.base, 0.4)
      },
      border: {
        default: '#d9d9cf',
        hover: '#d9d9cf',
        active: '#d9d9cf',
        disabled: '#c5c5c5'
      }
    })};

      background-image: linear-gradient(180deg, #efefeb 0%, #e4e4dd 100%);
      box-shadow: inset 0 -1px 0 #c2c2b2;
      text-shadow: 0 1px 0 #fff;

      &:hover:not([disabled]) {
        background-image: linear-gradient(180deg, #fbfbf9 0%, #efefeb 100%);
      }
    `,
    selected: css`
      ${getTertiaryButtonSelectedTokens({
      bg: colors.brand.base,
      fg: '#912680',
      border: '#d9d9cf'
    })};
    `,
    disabled: css`
      ${getTertiaryButtonDisabledTokens({
      bg: disabledColor(colors.shades.white),
      fg: disabledColor(colors.brand.base, 0.4),
      border: '#d9d9cf'
    })};

      cursor: not-allowed;
    `
  },
  destructive: {
    normal: css`
      ${baseButtonStyles};

      ${getDestructiveButtonTokens({
      bg: {
        default: '#e4e4dd',
        hover: '#efefeb',
        active: colors.error,
        disabled: disabledColor(colors.shades.white)
      },
      fg: {
        default: '#c90400',
        hover: '',
        active: activeColor(colors.error),
        disabled: disabledColor(colors.error, 0.4)
      },
      border: {
        default: '#d9d9cf',
        hover: '#d9d9cf',
        active: '#d9d9cf',
        disabled: '#c5c5c5'
      }
    })};

      background-image: linear-gradient(180deg, #efefeb 0%, #e4e4dd 100%);
      box-shadow: inset 0 -1px 0 #c2c2b2;
      text-shadow: 0 1px 0 #fff;

      &:hover:not([disabled]) {
        background-image: linear-gradient(180deg, #fbfbf9 0%, #efefeb 100%);
      }

      &:active:not([disabled]) {
      }
    `,
    selected: css`
      ${getDestructiveButtonSelectedTokens({
      bg: colors.error,
      fg: activeColor(colors.error),
      border: '#d9d9cf'
    })};
    `,
    disabled: css`
      ${getDestructiveButtonDisabledTokens({
      bg: disabledColor(colors.shades.white),
      fg: disabledColor(colors.error, 0.4),
      border: '#c5c5c5'
    })};

      cursor: not-allowed;
    `
  },
  small: css`
    ${getSmallButtonTokens({
    fontFamily: fonts.dinFont,
    fontSize: typeScale[7],
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.tighter,
    letterSpacing: tracking.normal,
    textTransform: 'none',
    paddingBlock: spacing[2],
    paddingInline: spacing[3],
    gap: spacing[2]
  })}
  `,
  medium: css`
    ${getMediumButtonTokens({
    fontFamily: fonts.dinFont,
    fontSize: typeScale[7],
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.tighter,
    letterSpacing: tracking.normal,
    textTransform: 'none',
    paddingBlock: spacing[3],
    paddingInline: spacing[5],
    gap: spacing[2]
  })};
  `,
  large: css`
    ${getLargeButtonTokens({
    fontFamily: fonts.dinFont,
    fontSize: typeScale[5],
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.tighter,
    letterSpacing: tracking.normal,
    textTransform: 'none',
    paddingBlock: spacing[4],
    paddingInline: spacing[5],
    gap: spacing[2]
  })};
  `
};
const baseFormValues = {
  borderColor: colors.shades.dark,
  borderColorValid: colors.success,
  borderColorInvalid: colors.error,
  selectedToggleColor: colors.brand.base,
  helpTextColor: colors.shades.darker,
  helpTextSize: typeScale[10],
  labelColor: colors.brand.dark,
  labelSize: typeScale[8],
  errorMessageTextColor: colors.error,
  infoMessageTextColor: colors.info
};
const real = {
  ...common,
  typeScale,
  fonts,
  colors,
  focus,
  id: 'real',
  mode: {
    form: 'light' //default
  },
  headings: {
    fontFamily: fonts.dinFont,
    color: 'currentColor',
    lineHeight: lineHeight.tighter,
    h1: {
      fontFamily: fonts.dinFont,
      fontSize: typeScale[1],
      fontWeight: fontWeight.medium,
      lineHeight: lineHeight.tighter,
      tracking: tracking.normal,
      uppercase: false
    },
    h2: {
      fontFamily: fonts.dinFont,
      fontSize: typeScale[2],
      lineHeight: lineHeight.tighter,
      fontWeight: fontWeight.medium,
      tracking: tracking.normal,
      uppercase: false
    },
    h3: {
      fontFamily: fonts.dinFont,
      fontSize: typeScale[3],
      lineHeight: lineHeight.tighter,
      fontWeight: fontWeight.regular,
      tracking: tracking.normal,
      uppercase: false
    },
    h4: {
      fontFamily: fonts.dinFont,
      fontSize: typeScale[5],
      lineHeight: lineHeight.tight,
      fontWeight: fontWeight.regular,
      tracking: tracking.normal,
      uppercase: false
    },
    h5: {
      fontFamily: fonts.dinFont,
      fontSize: typeScale[6],
      lineHeight: lineHeight.tight,
      fontWeight: fontWeight.regular,
      tracking: tracking.normal,
      uppercase: false
    },
    h6: {
      fontFamily: fonts.dinFont,
      fontSize: typeScale[8],
      lineHeight: lineHeight.tight,
      fontWeight: fontWeight.regular,
      tracking: tracking.normal,
      uppercase: false
    }
  },
  copy: {
    color: colors.shades.darkest,
    fontFamily: fonts.arialFont,
    fontSize: typeScale[8],
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.normal,
    smallFontSize: typeScale[10],
    largeFontSize: typeScale[6]
  },
  link: {
    loudColor: colors.brand.base,
    darkColor: colors.shades.darkest,
    lightColor: colors.shades.white,
    destructiveColor: colors.error
  },
  button: {
    small: buttonStyles.small,
    medium: buttonStyles.medium,
    large: buttonStyles.large,
    primary: buttonStyles.primary,
    secondary: buttonStyles.secondary,
    tertiary: buttonStyles.tertiary,
    destructive: buttonStyles.destructive
  },
  forms: {
    light: {
      ...baseFormValues,
      backgroundColor: colors.shades.lightest,
      inputBackgroundColor: colors.shades.white,
      boxShadow: `0 4px 8px 0 ${colors.shades.lighter}`
    },
    white: {
      ...baseFormValues,
      backgroundColor: colors.shades.white,
      inputBackgroundColor: colors.shades.lightest,
      boxShadow: 'none'
    }
  },
  smallFooter: {
    backgroundColor: '#2b2b2b',
    textColor: colors.shades.lighter,
    linkColor: colors.shades.lighter
  },
  panel: {
    loud: {
      backgroundColor: colors.brand.base,
      textColor: colors.shades.white
    },
    muted: {
      backgroundColor: colors.shades.lighter,
      textColor: colors.shades.black
    },
    light: {
      backgroundColor: colors.shades.lightest,
      textColor: colors.shades.black
    },
    dark: {
      backgroundColor: colors.shades.darkest,
      textColor: colors.shades.white
    },
    white: {
      backgroundColor: colors.shades.white,
      textColor: colors.shades.black
    }
  }
};
export default real;